/**
 * Active loans between funds.
 *
 * This is the answer to "онзи ден дадох заем от личните на входа" — without a
 * standing reminder, a transfer made in a hurry is indistinguishable from a
 * permanent contribution a week later. It stays on the dashboard, with its
 * age, until it is repaid.
 */
import { ArrowRight, HandCoins } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/misc';
import { formatAge, formatDate, formatMoney, formatPercent } from '@/lib/format';
import type { Loan } from '@/lib/types';

export function LoansPanel({
  loans,
  onRepay,
}: {
  loans: Loan[];
  onRepay: (loan: Loan) => void;
}): JSX.Element | null {
  if (loans.length === 0) return null;

  return (
    <Card className="border-amber-500/30">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <HandCoins className="h-4 w-4 text-amber-500" />
          Дългове между касите
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loans.map((loan) => {
          const progress =
            loan.principalMinor > 0 ? loan.repaidMinor / loan.principalMinor : 0;
          return (
            <div key={loan.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                    <span className="truncate">{loan.toFundName}</span>
                    <span className="text-muted-foreground">дължи на</span>
                    <span className="truncate">{loan.fromFundName}</span>
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatAge(loan.ageDays)}</span>
                    {loan.dueAt ? (
                      <>
                        <span aria-hidden>·</span>
                        <span className={cn(loan.overdue && 'text-destructive')}>
                          срок {formatDate(loan.dueAt)}
                        </span>
                      </>
                    ) : null}
                    {loan.overdue ? (
                      <Badge variant="destructive" className="text-[10px]">
                        просрочен
                      </Badge>
                    ) : null}
                  </p>
                </div>

                <div className="text-right">
                  <p className="tabular text-lg font-semibold">
                    {formatMoney(loan.outstandingMinor, loan.currency, loan.decimals)}
                  </p>
                  {loan.repaidMinor > 0 ? (
                    <p className="tabular text-xs text-muted-foreground">
                      от {formatMoney(loan.principalMinor, loan.currency, loan.decimals)}
                    </p>
                  ) : null}
                </div>
              </div>

              {loan.repaidMinor > 0 ? (
                <div className="mt-2">
                  <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-income transition-all"
                      style={{ width: `${Math.min(progress * 100, 100)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    върнати {formatPercent(progress)}
                  </p>
                </div>
              ) : null}

              <Button
                variant="outline"
                size="sm"
                className="mt-3 w-full gap-2 sm:w-auto"
                onClick={() => onRepay(loan)}
              >
                Върни заема
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
